<script setup lang="ts">
import {computed, ref} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupScanCard from "@/medication-checkup/components/MedicationCheckupScanCard.vue";
import MedicationCheckupAlertsRecap from "@/medication-checkup/components/MedicationCheckupAlertsRecap.vue";
import MedicationCheckupAlertSourceRow from "@/medication-checkup/components/MedicationCheckupAlertSourceRow.vue";
import MedicationCheckupAlertLevelDialog from "@/medication-checkup/components/MedicationCheckupAlertLevelDialog.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {
    AlertLevel,
    AlertSource,
    AlertsView
} from "@/medication-checkup/interfaces/alerts";
import {BEAT_MS, TYPING_MS} from "@/medication-checkup/constants";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useScanRun} from "@/medication-checkup/composables/useScanRun";

/**
 * The scene of the vigilance alerts: the analysis of the treatments run over
 * the sources, and what they raised, read source by source and level by
 * level before the pharmacist crosses over to the synthesis.
 *
 * A first visit runs the analysis behind the card, source by source as the
 * stream reports them, then reveals the recap and one row of cards per
 * source; a later visit finds the run served and shows it whole. Every
 * word is served by the handler — the recap, the sources, the levels, the
 * alerts, the action; the scene owns the pacing and the colours only.
 */

const props = defineProps<SceneProps<AlertsView>>();

/**
 * The action asking the server for the analysis plainly, without the stream.
 */
const RUN_ACTION = 'run';

/**
 * A frozen scene renders the run whole; a live one runs the analysis when
 * none was run yet, and reveals the run served otherwise.
 */
const {bubbleShown, clicked, view, text, actions, closing, acted, later, act} = useScene(props, {
    frozen: () => revealStep.value = 3,
    live: () => {
        if (served.value === null) {
            analyze();

            return;
        }

        later(() => reveal(), TYPING_MS + BEAT_MS);
    }
});

const store = useMedicationCheckupStore();

/**
 * Whether this visit runs the analysis, so the card is shown; a visit that
 * finds a run served shows the rows without it.
 */
const analysing = ref(false);

/**
 * The level whose whole list is open in the dialog, with its source.
 */
const opened = ref<{source: AlertSource; level: AlertLevel} | null>(null);

const lines = computed(() => view.value?.lines ?? []);

const served = computed(() => view.value?.run ?? null);

const {
    run,
    done: analysisDone,
    failed: analysisFailed,
    progress,
    revealStep,
    reveal,
    start
} = useScanRun({
    bubbleShown,
    later,
    stream: (report, signal) => store.analyzeBlock(props.block.id, report, signal),
    fallback: () => store.submitBlock(props.block.id, {action: RUN_ACTION})
});

/**
 * Run the analysis behind the card, which stays up for as long as this visit
 * is the one running it.
 */
const analyze = (): Promise<void> => {
    analysing.value = true;

    return start();
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen && !analysing">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <template v-if="served">
        <MedicationCheckupAlertsRecap :run="served" />
        <MedicationCheckupAlertSourceRow
          v-for="source in served.sources"
          :key="source.key"
          :source="source"
          :lines="lines"
          @open="opened = {source, level: $event}"
        />
      </template>
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupScanCard
        v-if="analysing && bubbleShown"
        title="L'IA Apodis analyse les traitements"
        retry-label="L'analyse a échoué — réessayer"
        :failed="analysisFailed"
        :done="analysisDone"
        :progress="progress"
        :run="run"
        class="animate-message-in"
        @retry="analyze"
      />

      <template v-if="served && revealStep >= 1">
        <MedicationCheckupAlertsRecap :run="served" class="animate-message-in" />

        <template v-if="revealStep >= 2">
          <MedicationCheckupAlertSourceRow
            v-for="source in served.sources"
            :key="source.key"
            :source="source"
            :lines="lines"
            class="animate-message-in"
            @open="opened = {source, level: $event}"
          />
        </template>

        <MedicationCheckupCopiloteLine
          v-if="revealStep >= 3 && closing"
          :text="closing"
          class="animate-message-in"
        />

        <MedicationCheckupSceneReplies
          :clicked="clicked"
          :actions="actions"
          :shown="revealStep >= 3"
          :echo="acted"
          @act="act"
        />
      </template>
    </template>

    <MedicationCheckupAlertLevelDialog
      :level="opened?.level ?? null"
      :source="opened?.source ?? null"
      :lines="lines"
      @close="opened = null"
    />
  </div>
</template>
